import React, { useState, useEffect } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { FaUsers, FaUserClock, FaUserPlus, FaCalendarCheck, FaUserGraduate, FaHandshake, FaFileInvoiceDollar, FaAward, FaCalendarAlt, FaFilter, FaUserCheck, FaBirthdayCake, FaWhatsapp, FaMagic } from 'react-icons/fa';
import { MdRefresh } from 'react-icons/md';
import axios from 'axios';

const COLORS = ['#10b981', '#f59e0b', '#ef4444', '#6b7280']; 

interface DashbordOverviewProps {
  onNavigateToMembers?: (filter: string) => void;
  onNavigateTab?: (tab: string) => void;
}

const DashbordOverview: React.FC<DashbordOverviewProps> = ({ onNavigateToMembers, onNavigateTab }) => {
  const [stats, setStats] = useState<{
    total_members: number;
    active_members?: number;
    pending_approval: number;
    inactive_members: number;
    deleted_members: number;
    new_members: number;
    renewals_pending: number;
    grace_period: number;
    expired: number;
  }>({
    total_members: 0,
    active_members: 0,
    pending_approval: 0,
    inactive_members: 0,
    deleted_members: 0,
    new_members: 0,
    renewals_pending: 0,
    grace_period: 0,
    expired: 0
  });

  const [societyYear, setSocietyYear] = useState('2026-2027');
  const [selectedMonth, setSelectedMonth] = useState('ALL');

  const [activityMetrics, setActivityMetrics] = useState({
    r2r_count: 0,
    business_generated_amount: 0,
    visitor_count: 0,
    referral_count: 0
  });

  const [flaggedMembers, setFlaggedMembers] = useState<any[]>([]);
  const [upcomingBirthdays, setUpcomingBirthdays] = useState<any[]>([]);

  const [monthlyChartData] = useState<any[]>([
    { name: 'Jul', newMembers: 2, renewals: 4 },
    { name: 'Aug', newMembers: 3, renewals: 2 },
    { name: 'Sep', newMembers: 4, renewals: 5 },
    { name: 'Oct', newMembers: 1, renewals: 3 },
    { name: 'Nov', newMembers: 5, renewals: 6 },
    { name: 'Dec', newMembers: 2, renewals: 4 },
    { name: 'Jan', newMembers: 4, renewals: 3 },
    { name: 'Feb', newMembers: 3, renewals: 2 },
    { name: 'Mar', newMembers: 6, renewals: 5 },
    { name: 'Apr', newMembers: 2, renewals: 4 },
    { name: 'May', newMembers: 4, renewals: 3 },
    { name: 'Jun', newMembers: 5, renewals: 7 },
  ]);

  const fetchFlaggedMembers = async () => {
    try {
      const res = await axios.get('https://rmbbackend.rmbjaipur.com/auth/attendance/flagged-absent-members');
      setFlaggedMembers(res.data.flaggedMembers || []);
    } catch {
      // Silent — absence API may not have data yet
    }
  };

  const fetchUpcomingBirthdays = async () => {
    try {
      const res = await axios.get('https://rmbbackend.rmbjaipur.com/auth/upcoming-birthdays');
      setUpcomingBirthdays(res.data.upcomingBirthdays || []);
    } catch {
      // Silent
    }
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get('https://rmbbackend.rmbjaipur.com/auth/dashboard-stats');
      setStats(res.data);
    } catch {
      // Silent
    }
  };

  const fetchActivityMetrics = async () => {
    try {
      const res = await axios.get('https://rmbbackend.rmbjaipur.com/auth/dashboard-activity-metrics', {
        params: { society_year: societyYear, month: selectedMonth }
      });
      setActivityMetrics(res.data);
    } catch {
      // Silent
    }
  };

  useEffect(() => {
    fetchStats();
    fetchActivityMetrics();
    fetchFlaggedMembers();
    fetchUpcomingBirthdays();

    const interval = setInterval(() => {
      fetchStats();
      fetchActivityMetrics();
      fetchFlaggedMembers();
      fetchUpcomingBirthdays();
    }, 30000);

    return () => clearInterval(interval);
  }, [societyYear, selectedMonth]);

  const activeMembersCount = (stats as any).active_members !== undefined ? (stats as any).active_members : Math.max(0, stats.total_members - (stats.inactive_members || 0));

  const pieData = [
    { name: 'Active Members', value: activeMembersCount },
    { name: 'Grace Period (45d)', value: stats.grace_period },
    { name: 'Inactive Members', value: stats.inactive_members },
    { name: 'Deleted (Trash)', value: stats.deleted_members },
  ];

  return (
    <div className="p-6 bg-[#f4f7f6] min-h-screen space-y-6">
      {/* Top Header */}
      <div className="bg-white dark:bg-[#1E2B45] p-4 sm:p-6 rounded-2xl shadow-sm border border-slate-200/80 dark:border-[#2A3B5C] flex flex-col md:flex-row justify-between items-start md:items-center gap-3 sm:gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-black text-[#0A2451] dark:text-white">RMB Jaipur Executive Dashboard</h1>
          <p className="text-[11px] sm:text-xs text-gray-500 dark:text-slate-400 mt-1">Real-time society year metrics, business generated, and membership distribution</p>
        </div>

        <div className="flex items-center gap-2 sm:gap-3 w-full md:w-auto">
          <button 
            onClick={() => { fetchStats(); fetchActivityMetrics(); fetchFlaggedMembers(); }}
            className="flex-1 md:flex-none justify-center bg-[#0A2451] text-amber-400 px-4 py-2.5 rounded-xl shadow-md hover:bg-blue-900 transition flex items-center gap-1.5 text-xs font-black cursor-pointer border border-blue-400/20"
          >
            <MdRefresh size={18} /> Refresh Stats
          </button>
        </div>
      </div>

      {/* ⚠️ 3+ CONTINUOUS ABSENCES WARNING CARD FOR LEADERSHIP */}
      {flaggedMembers.length > 0 && (
        <div className="bg-gradient-to-r from-red-600 via-rose-600 to-amber-600 p-4 sm:p-5 rounded-3xl shadow-xl text-white space-y-3">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center border-b border-white/20 pb-2 gap-2">
            <h3 className="text-xs sm:text-sm font-black flex items-center gap-2 uppercase tracking-wide">
              ⚠️ ATTENTION LEADERSHIP: {flaggedMembers.length} MEMBER(S) WITH 3+ CONTINUOUS ABSENCES
            </h3>
            <span className="text-[10px] bg-white text-red-700 font-extrabold px-3 py-1 rounded-full uppercase shadow-xs">Action Required</span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            {flaggedMembers.map((m: any) => (
              <div key={m.id} className="bg-white/15 backdrop-blur-md p-3 rounded-2xl border border-white/20 flex items-center gap-3">
                <img
                  src={m.profile ? (m.profile.startsWith('http') ? m.profile : `https://rmbbackend.rmbjaipur.com/${m.profile}`) : 'https://via.placeholder.com/150'}
                  alt={m.name}
                  className="w-10 h-10 rounded-full object-cover border-2 border-white/50"
                />
                <div>
                  <h4 className="font-extrabold text-white text-xs leading-snug">{m.name}</h4>
                  <p className="text-[10px] text-red-100 font-medium">{m.company_name || 'Member'}</p>
                  <div className="text-[10px] font-black text-amber-300 mt-0.5">
                    🚨 {m.consecutive_absences} Consecutive Absences
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Society Year & Month Filter Controls Bar */}
      <div className="bg-white dark:bg-[#1E2B45] p-3.5 sm:p-4 rounded-2xl shadow-sm border border-slate-200/80 dark:border-[#2A3B5C] flex flex-col md:flex-row justify-between items-start md:items-center gap-3">
        <div className="flex items-center gap-2">
          <FaFilter className="text-[#0A2451] dark:text-amber-400" />
          <span className="text-xs font-bold text-gray-700 dark:text-slate-300 uppercase tracking-wider">Society Year & Month Filters:</span>
        </div>

        <div className="flex flex-wrap items-center gap-2.5 w-full md:w-auto">
          <div className="flex items-center gap-1.5 bg-gray-50 dark:bg-slate-800 p-1.5 rounded-xl border border-slate-200/80 dark:border-slate-700 flex-1 sm:flex-none">
            <span className="text-[11px] font-bold text-gray-500 dark:text-slate-400 pl-1">Society Year:</span>
            <select
              value={societyYear}
              onChange={(e) => setSocietyYear(e.target.value)}
              className="py-1 px-2.5 outline-none text-xs font-bold text-[#0A2451] dark:text-amber-300 bg-white dark:bg-slate-900 rounded-lg border border-gray-300 dark:border-slate-700"
            >
              <option value="2023-2024">2023-2024</option>
              <option value="2024-2025">2024-2025</option>
              <option value="2025-2026">2025-2026</option>
              <option value="2026-2027">2026-2027</option>
            </select>
          </div>

          <div className="flex items-center gap-1.5 bg-gray-50 dark:bg-slate-800 p-1.5 rounded-xl border border-slate-200/80 dark:border-slate-700 flex-1 sm:flex-none">
            <span className="text-[11px] font-bold text-gray-500 dark:text-slate-400 pl-1">Month:</span>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="py-1 px-2.5 outline-none text-xs font-bold text-gray-800 dark:text-slate-200 bg-white dark:bg-slate-900 rounded-lg border border-gray-300 dark:border-slate-700"
            >
              <option value="ALL">Complete Society Year (All Months)</option>
              <option value="7">July</option>
              <option value="8">August</option>
              <option value="9">September</option>
              <option value="10">October</option>
              <option value="11">November</option>
              <option value="12">December</option>
              <option value="1">January</option>
              <option value="2">February</option>
              <option value="3">March</option>
              <option value="4">April</option>
              <option value="5">May</option>
              <option value="6">June</option>
            </select>
          </div>
        </div>
      </div>

      {/* Row 1: Filtered Activity & Business Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
        {/* R2R Meetings Card */}
        <div 
          onClick={() => onNavigateTab && onNavigateTab('ReportsAnalytics')}
          className="bg-gradient-to-br from-purple-800 to-indigo-700 p-5 rounded-2xl shadow-lg text-white relative overflow-hidden flex flex-col justify-between h-32 cursor-pointer hover:scale-[1.02] transition-all"
        >
          <div className="flex justify-between items-start">
            <div className="bg-white/20 p-2 rounded-lg">
              <FaUserCheck size={22} />
            </div>
            <div className="text-right">
              <h2 className="text-3xl font-black">{activityMetrics.r2r_count}</h2>
              <p className="text-xs font-bold opacity-90">R2R One-on-Ones</p>
            </div>
          </div>
          <p className="text-[11px] text-right mt-1 opacity-80">Logged in {societyYear} (Click for Details)</p>
          <div className="absolute bottom-0 left-0 h-1 bg-white/40 w-full" />
        </div>

        {/* Business Generated Card */}
        <div 
          onClick={() => onNavigateTab && onNavigateTab('ReportsAnalytics')}
          className="bg-gradient-to-r from-emerald-600 to-teal-500 p-5 rounded-xl shadow-md text-white relative overflow-hidden flex flex-col justify-between h-32 cursor-pointer hover:scale-[1.02] transition-transform"
        >
          <div className="flex justify-between items-start">
            <div className="bg-white/20 p-2 rounded-lg">
              <FaFileInvoiceDollar size={22} />
            </div>
            <div className="text-right">
              <h2 className="text-2xl font-black">₹{Number(activityMetrics.business_generated_amount).toLocaleString()}</h2>
              <p className="text-xs font-bold opacity-90">Business Closed (TYFCB)</p>
            </div>
          </div>
          <p className="text-[11px] text-right mt-1 opacity-80">Total value generated (Click for Details)</p>
          <div className="absolute bottom-0 left-0 h-1 bg-white/40 w-full" />
        </div>

        {/* Visitors Invited Card */}
        <div 
          onClick={() => onNavigateTab && onNavigateTab('ActivityVerification')}
          className="bg-gradient-to-r from-amber-500 to-orange-500 p-5 rounded-xl shadow-md text-white relative overflow-hidden flex flex-col justify-between h-32 cursor-pointer hover:scale-[1.02] transition-transform"
        >
          <div className="flex justify-between items-start">
            <div className="bg-white/20 p-2 rounded-lg">
              <FaUserPlus size={22} />
            </div>
            <div className="text-right">
              <h2 className="text-3xl font-black">{activityMetrics.visitor_count}</h2>
              <p className="text-xs font-bold opacity-90">Visitors / Guests</p>
            </div>
          </div>
          <p className="text-[11px] text-right mt-1 opacity-80">Invited by members (Click for Details)</p>
          <div className="absolute bottom-0 left-0 h-1 bg-white/40 w-full" />
        </div>

        {/* Referrals Passed Card */}
        <div 
          onClick={() => onNavigateTab && onNavigateTab('ActivityVerification')}
          className="bg-gradient-to-r from-blue-600 to-cyan-600 p-5 rounded-xl shadow-md text-white relative overflow-hidden flex flex-col justify-between h-32 cursor-pointer hover:scale-[1.02] transition-transform"
        >
          <div className="flex justify-between items-start">
            <div className="bg-white/20 p-2 rounded-lg">
              <FaHandshake size={22} />
            </div>
            <div className="text-right">
              <h2 className="text-3xl font-black">{activityMetrics.referral_count}</h2>
              <p className="text-xs font-bold opacity-90">Referrals Passed</p>
            </div>
          </div>
          <p className="text-[11px] text-right mt-1 opacity-80">Passed in chapter (Click for Details)</p>
          <div className="absolute bottom-0 left-0 h-1 bg-white/40 w-full" />
        </div>
      </div>

      {/* Row 2: Roster Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        <div 
          onClick={() => onNavigateToMembers && onNavigateToMembers('active')}
          className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 cursor-pointer hover:border-emerald-400 transition"
        >
          <div className="flex justify-between items-center">
            <div>
              <p className="text-[11px] font-bold text-gray-500 uppercase">Active Strength</p>
              <h3 className="text-2xl font-black text-emerald-600 mt-0.5">{activeMembersCount}</h3>
            </div>
            <div className="bg-emerald-50 text-emerald-600 p-3 rounded-xl border border-emerald-200">
              <FaUsers size={20} />
            </div>
          </div>
        </div>

        <div 
          onClick={() => onNavigateToMembers && onNavigateToMembers('grace')}
          className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 cursor-pointer hover:border-amber-400 transition"
        >
          <div className="flex justify-between items-center">
            <div>
              <p className="text-[11px] font-bold text-gray-500 uppercase">Grace Period (45d)</p>
              <h3 className="text-2xl font-black text-amber-600 mt-0.5">{stats.grace_period}</h3>
            </div>
            <div className="bg-amber-50 text-amber-600 p-3 rounded-xl border border-amber-200">
              <FaUserClock size={20} />
            </div>
          </div>
        </div>

        <div 
          onClick={() => onNavigateToMembers && onNavigateToMembers('inactive')}
          className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 cursor-pointer hover:border-rose-400 transition"
        >
          <div className="flex justify-between items-center">
            <div>
              <p className="text-[11px] font-bold text-gray-500 uppercase">Inactive Members</p>
              <h3 className="text-2xl font-black text-rose-600 mt-0.5">{stats.inactive_members}</h3>
            </div>
            <div className="bg-rose-50 text-rose-600 p-3 rounded-xl border border-rose-200">
              <FaUsers size={20} />
            </div>
          </div>
        </div>

        <div 
          onClick={() => onNavigateToMembers && onNavigateToMembers('deleted')}
          className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 cursor-pointer hover:border-gray-400 transition"
        >
          <div className="flex justify-between items-center">
            <div>
              <p className="text-[11px] font-bold text-gray-500 uppercase">Deleted (Trash)</p>
              <h3 className="text-2xl font-black text-gray-700 mt-0.5">{stats.deleted_members}</h3>
            </div>
            <div className="bg-gray-100 text-gray-700 p-3 rounded-xl border border-gray-300">
              <FaUserGraduate size={20} />
            </div>
          </div>
        </div>
      </div>

      {/* Row 3: Dynamic Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Area Chart: New Members & Renewals by Month */}
        <div className="lg:col-span-2 bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <div className="flex justify-between items-center mb-5 border-b pb-3">
            <div>
              <h3 className="text-base font-bold text-gray-800">New Member Joinings & Tenure Renewals Graph</h3>
              <p className="text-xs text-gray-500">Monthly breakdown for Society Year: {societyYear}</p>
            </div>
          </div>
          
          <div className="h-72 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={monthlyChartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorNew" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorRen" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f97316" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#f97316" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                <XAxis dataKey="name" tick={{fill: '#6b7280', fontSize: 11}} tickLine={false} axisLine={{stroke: '#e5e7eb'}} />
                <YAxis tick={{fill: '#6b7280', fontSize: 11}} tickLine={false} axisLine={false} />
                <Tooltip />
                <Legend iconType="rect" wrapperStyle={{paddingTop: '15px'}} />
                <Area type="monotone" name="New Members Joined" dataKey="newMembers" stroke="#3b82f6" strokeWidth={3} fillOpacity={1} fill="url(#colorNew)" dot={{r: 4, strokeWidth: 2, fill: '#fff', stroke: '#3b82f6'}} activeDot={{r: 6}} />
                <Area type="monotone" name="Tenure Renewals" dataKey="renewals" stroke="#f97316" strokeWidth={3} fillOpacity={1} fill="url(#colorRen)" dot={{r: 4, strokeWidth: 2, fill: '#fff', stroke: '#f97316'}} activeDot={{r: 6}} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Pie Chart: Dynamic Member Distribution */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 flex flex-col justify-between">
          <div className="flex justify-between items-start border-b pb-3 mb-2">
            <div>
              <h3 className="text-base font-bold text-gray-800">Member Distribution</h3>
              <p className="text-xs text-gray-500">Live roster segment status</p>
            </div>
            <div className="text-right">
              <span className="text-[10px] text-gray-500 uppercase tracking-wider block font-bold">Total Roster</span>
              <span className="text-2xl font-black text-[#0A2451]">{stats.total_members}</span>
            </div>
          </div>
          
          <div className="h-[250px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={55}
                  outerRadius={90}
                  paddingAngle={3}
                  dataKey="value"
                  stroke="#fff"
                  strokeWidth={2}
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend 
                  layout="horizontal" 
                  verticalAlign="bottom" 
                  align="center"
                  iconType="circle"
                  formatter={(value, entry: any) => (
                    <span className="text-xs text-gray-700 font-medium">{value}: <b className="text-gray-900">{entry.payload.value}</b></span>
                  )}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Row 4: 🎂 Upcoming Member Birthdays */}
      <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-200 space-y-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 border-b pb-3">
          <div>
            <h3 className="text-base font-extrabold text-[#0A2451] flex items-center gap-2">
              <FaBirthdayCake className="text-amber-500" /> Upcoming Member Birthdays
            </h3>
            <p className="text-xs text-gray-500">Celebrate active chapter members' birthdays and send instant greetings</p>
          </div>
          <span className="bg-amber-100 text-amber-800 font-extrabold text-xs px-3 py-1 rounded-full border border-amber-200">
            {upcomingBirthdays.length} Member Birthdays Scheduled
          </span>
        </div>

        {upcomingBirthdays.length === 0 ? (
          <p className="text-xs text-gray-400 italic py-4 text-center">No upcoming birthdays found in the system.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {upcomingBirthdays.slice(0, 8).map((member: any) => {
              const isToday = member.daysUntil === 0;
              const isTomorrow = member.daysUntil === 1;

              return (
                <div
                  key={member.id}
                  className={`p-4 rounded-xl border transition flex flex-col justify-between space-y-3 ${
                    isToday
                      ? 'bg-gradient-to-br from-amber-50 to-pink-50 border-amber-400 shadow-md ring-2 ring-amber-300'
                      : isTomorrow
                      ? 'bg-amber-50/50 border-amber-200'
                      : 'bg-slate-50/70 border-slate-200 hover:border-amber-300'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <img
                      src={
                        member.profile
                          ? member.profile.startsWith('http')
                            ? member.profile
                            : `https://rmbbackend.rmbjaipur.com/${member.profile.replace(/\\/g, '/').replace(/^\/+/, '')}`
                          : 'https://via.placeholder.com/150'
                      }
                      alt={member.name}
                      className="w-12 h-12 rounded-full object-cover border-2 border-amber-400 shadow-sm"
                    />
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-black text-slate-800 truncate">{member.name}</h4>
                      <p className="text-xs text-slate-500 truncate">{member.designation || member.company_name || 'RMB Member'}</p>
                      <div className="flex items-center gap-1.5 mt-1">
                        <span className="text-[10px] font-bold bg-white text-slate-700 px-2 py-0.5 rounded border border-slate-200">
                          📅 {member.formattedDate}
                        </span>
                        <span
                          className={`text-[10px] font-black px-2 py-0.5 rounded uppercase ${
                            isToday
                              ? 'bg-amber-500 text-slate-950 animate-pulse'
                              : isTomorrow
                              ? 'bg-amber-200 text-amber-900'
                              : 'bg-slate-200 text-slate-700'
                          }`}
                        >
                          {member.statusText}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 pt-2 border-t border-slate-200/60">
                    <button
                      onClick={() => {
                        const msg = encodeURIComponent(
                          `🎂 *Happy Birthday Rotarian ${member.name}!* 🎉\n\nOn behalf of the entire RMB Jaipur Chapter, we wish you joy, success, and continuous prosperity!\n\n_Rotary Means Business Fellowship_`
                        );
                        window.open(`https://api.whatsapp.com/send?text=${msg}`, '_blank');
                      }}
                      className="flex-1 py-1.5 px-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-[11px] rounded-lg shadow-sm transition flex items-center justify-center gap-1 cursor-pointer"
                    >
                      <FaWhatsapp size={12} /> Wish
                    </button>

                    <button
                      onClick={() => {
                        if (onNavigateTab) onNavigateTab('chatgpt');
                      }}
                      className="py-1.5 px-2.5 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-slate-950 font-black text-[11px] rounded-lg shadow-sm transition flex items-center gap-1 cursor-pointer"
                      title="Create Birthday Flyer Banner in ChatGPT Studio"
                    >
                      <FaMagic size={11} /> Flyer
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default DashbordOverview;
